import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock3,
  ListPlus,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  Users,
  XCircle,
} from 'lucide-react';
import type {
  ForfeoReserveAvailabilitySearchResult,
  ForfeoReserveBookingContract,
  ForfeoReserveHoldContract,
} from '../../../supabase/functions/_shared/forfeo-reserve-booking';
import {
  applyForfeoReserveBookingCommand,
  createForfeoReserveAvailabilitySearch,
  createForfeoReserveBookingCommand,
  fetchForfeoReserveBookingSnapshot,
  searchForfeoReserveAvailability,
} from '../../lib/forfeoReserveBookingClient';

interface Props {
  businessId: string;
  onUpdate?: () => void;
}

function toLocalInput(date: Date): string {
  const adjusted = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return adjusted.toISOString().slice(0, 16);
}

function toIso(value: string): string {
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) throw new Error('invalid_booking_window');
  return parsed.toISOString();
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('fr-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function errorMessage(value: unknown): string {
  const code = value instanceof Error ? value.message : String(value);
  if (code.includes('capacity_conflict')) {
    return 'La capacité vient de changer. Actualisez puis choisissez un autre créneau.';
  }
  if (code.includes('revision_conflict')) {
    return 'Cette réservation a été modifiée ailleurs. Actualisez avant de recommencer.';
  }
  if (code.includes('idempotency_collision')) {
    return 'La commande a été refusée afin d’éviter une double réservation.';
  }
  if (code.includes('origin_forbidden')) {
    return 'L’origine du portail doit être autorisée avant l’activation de Reserve.';
  }
  if (code.includes('forbidden')) {
    return 'Votre rôle ne permet pas cette opération.';
  }
  return 'Le moteur Reserve n’a pas pu terminer cette opération.';
}

const HOLD_LABELS: Record<ForfeoReserveHoldContract['status'], string> = {
  active: 'En attente',
  consumed: 'Confirmé',
  released: 'Libéré',
  expired: 'Expiré',
};

const BOOKING_LABELS: Record<ForfeoReserveBookingContract['state'], string> = {
  pending_confirmation: 'À confirmer',
  pending_payment: 'Paiement requis',
  confirmed: 'Confirmée',
  completed: 'Terminée',
  cancelled: 'Annulée',
  expired: 'Expirée',
  no_show: 'Absence',
  manual_review: 'Révision manuelle',
};

const ReserveBookingEngine: React.FC<Props> = ({ businessId, onUpdate }) => {
  const now = useMemo(() => new Date(), []);
  const defaultEnd = useMemo(() => new Date(now.getTime() + 60 * 60 * 1000), [now]);
  const [snapshot, setSnapshot] = useState<Awaited<ReturnType<
    typeof fetchForfeoReserveBookingSnapshot
  >> | null>(null);
  const [catalogItemId, setCatalogItemId] = useState('');
  const [startsAt, setStartsAt] = useState(toLocalInput(now));
  const [endsAt, setEndsAt] = useState(toLocalInput(defaultEnd));
  const [partySize, setPartySize] = useState(1);
  const [availability, setAvailability] =
    useState<ForfeoReserveAvailabilitySearchResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadSnapshot = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rangeStart = new Date();
      rangeStart.setHours(0, 0, 0, 0);
      const rangeEnd = new Date(rangeStart);
      rangeEnd.setDate(rangeEnd.getDate() + 90);
      const next = await fetchForfeoReserveBookingSnapshot(
        businessId,
        rangeStart.toISOString(),
        rangeEnd.toISOString(),
      );
      setSnapshot(next);
      setCatalogItemId((current) => (
        next.catalogItems.some((item) => item.id === current && item.active)
          ? current
          : next.catalogItems.find((item) => item.active)?.id || ''
      ));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void loadSnapshot();
  }, [loadSnapshot]);

  const catalogLabels = useMemo(() => new Map(
    snapshot?.catalogItems.map((item) => [item.id, item.label]) || [],
  ), [snapshot]);

  const runCommand = async (
    command: Parameters<typeof createForfeoReserveBookingCommand>[0],
    success: string,
  ) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await applyForfeoReserveBookingCommand(
        createForfeoReserveBookingCommand(command),
      );
      setNotice(success);
      setAvailability(null);
      await loadSnapshot();
      onUpdate?.();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  };

  const searchAvailability = async () => {
    if (!catalogItemId) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const result = await searchForfeoReserveAvailability(
        createForfeoReserveAvailabilitySearch({
          businessId,
          catalogItemId,
          startsAt: toIso(startsAt),
          endsAt: toIso(endsAt),
          partySize,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      );
      setAvailability(result);
    } catch (caught) {
      setAvailability(null);
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  };

  const createHold = async () => {
    await runCommand({
      kind: 'create_hold',
      businessId,
      catalogItemId,
      startsAt: toIso(startsAt),
      endsAt: toIso(endsAt),
      partySize,
    }, 'Le créneau est retenu pendant dix minutes.');
  };

  const joinWaitlist = async () => {
    await runCommand({
      kind: 'join_waitlist',
      businessId,
      catalogItemId,
      startsAt: toIso(startsAt),
      endsAt: toIso(endsAt),
      partySize,
    }, 'La demande a été ajoutée à la liste d’attente.');
  };

  if (loading && !snapshot) {
    return (
      <div className="flex min-h-64 items-center justify-center text-slate-700">
        <Loader2 className="mr-3 animate-spin" /> Chargement du moteur Reserve…
      </div>
    );
  }

  return (
    <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm" data-forfeo-ui-v218="booking-engine" data-commerce-panel-v219="booking-engine" data-commerce-v220-panel="booking-engine">
      <header className="border-b border-slate-200 bg-white p-5 text-slate-900">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-blue-700">
              <CalendarClock size={17} /> Forfeo Reserve
            </div>
            <h2 className="mt-2 text-2xl font-black">Moteur de réservation</h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-700">
              Recherche, mise en attente atomique, confirmation gratuite et libération contrôlée de capacité.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void loadSnapshot()}
            disabled={loading || saving}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-2 text-sm font-bold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
        </div>
      </header>

      <div className="space-y-6 p-4 sm:p-6">
        <div className="flex items-start gap-3 rounded-2xl border border-cyan-100 bg-cyan-50 p-4 text-sm text-cyan-950">
          <ShieldCheck className="mt-0.5 shrink-0 text-cyan-700" size={20} />
          <p>
            Reserve calcule la capacité côté serveur. Aucun prix, acompte ou état Stripe n’est accepté depuis ce portail.
          </p>
        </div>

        {error && (
          <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-800">
            <AlertTriangle className="mt-0.5 shrink-0" size={20} /> {error}
          </div>
        )}
        {notice && (
          <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800">
            <CheckCircle2 className="mt-0.5 shrink-0" size={20} /> {notice}
          </div>
        )}

        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-2 xl:grid-cols-5">
          <label className="space-y-1 xl:col-span-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-700">Élément réservable</span>
            <select
              value={catalogItemId}
              onChange={(event) => {
                setCatalogItemId(event.target.value);
                setAvailability(null);
              }}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 font-semibold"
            >
              <option value="">Choisir…</option>
              {snapshot?.catalogItems.filter((item) => item.active).map((item) => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-xs font-black uppercase tracking-wider text-slate-700">Début</span>
            <input
              type="datetime-local"
              value={startsAt}
              onChange={(event) => {
                setStartsAt(event.target.value);
                setAvailability(null);
              }}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-black uppercase tracking-wider text-slate-700">Fin</span>
            <input
              type="datetime-local"
              value={endsAt}
              onChange={(event) => {
                setEndsAt(event.target.value);
                setAvailability(null);
              }}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-black uppercase tracking-wider text-slate-700">Capacité</span>
            <input
              type="number"
              min={1}
              max={100000}
              value={partySize}
              onChange={(event) => {
                setPartySize(Math.max(1, Number(event.target.value) || 1));
                setAvailability(null);
              }}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5"
            />
          </label>
          <div className="flex flex-wrap gap-2 md:col-span-2 xl:col-span-5">
            <button
              type="button"
              disabled={!catalogItemId || saving}
              onClick={() => void searchAvailability()}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-black text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              Vérifier la disponibilité
            </button>
            {availability && ['available', 'limited'].includes(availability.state) && (
              <button
                type="button"
                disabled={saving || availability.remainingCapacity < partySize}
                onClick={() => void createHold()}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <Clock3 size={16} /> Retenir 10 minutes
              </button>
            )}
            {availability && ['fully_booked', 'blocked', 'closed'].includes(availability.state) && (
              <button
                type="button"
                disabled={saving}
                onClick={() => void joinWaitlist()}
                className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-black text-slate-950 hover:bg-amber-400 disabled:opacity-50"
              >
                <ListPlus size={16} /> Ajouter à la liste d’attente
              </button>
            )}
          </div>
        </div>

        {availability && (
          <div className="grid gap-3 rounded-2xl border border-slate-200 p-4 sm:grid-cols-3">
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-slate-700">État</div>
              <div className="mt-1 text-lg font-black text-slate-900">{availability.state}</div>
            </div>
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-slate-700">Capacité restante</div>
              <div className="mt-1 text-lg font-black text-slate-900">{availability.remainingCapacity}</div>
            </div>
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-slate-700">Engagée</div>
              <div className="mt-1 text-lg font-black text-slate-900">{availability.committedCapacity}</div>
            </div>
          </div>
        )}

        <div className="grid gap-6 xl:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 p-4">
            <h3 className="flex items-center gap-2 text-lg font-black text-slate-900">
              <Clock3 size={19} className="text-cyan-700" /> Holds actifs
            </h3>
            <div className="mt-4 space-y-3">
              {snapshot?.holds.filter((hold) => hold.status === 'active').map((hold) => (
                <article key={hold.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="font-black text-slate-900">{catalogLabels.get(hold.catalogItemId) || 'Élément Reserve'}</div>
                      <div className="mt-1 text-sm text-slate-600">{formatDateTime(hold.startsAt)} – {formatDateTime(hold.endsAt)}</div>
                      <div className="mt-1 flex items-center gap-2 text-xs font-bold text-slate-700">
                        <Users size={14} /> {hold.partySize} · {HOLD_LABELS[hold.status]} · expire {formatDateTime(hold.expiresAt)}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => void runCommand({
                          kind: 'confirm_free',
                          businessId,
                          holdId: hold.id,
                          expectedRevision: hold.revision,
                        }, 'La réservation gratuite a été confirmée.')}
                        className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-black text-white disabled:opacity-50"
                      >
                        Confirmer sans paiement
                      </button>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => void runCommand({
                          kind: 'release_hold',
                          businessId,
                          holdId: hold.id,
                          expectedRevision: hold.revision,
                        }, 'La capacité a été libérée.')}
                        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-black text-slate-700 disabled:opacity-50"
                      >
                        Libérer
                      </button>
                    </div>
                  </div>
                </article>
              ))}
              {!snapshot?.holds.some((hold) => hold.status === 'active') && (
                <p className="rounded-xl bg-slate-50 p-4 text-sm font-semibold text-slate-700">Aucun hold actif.</p>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 p-4">
            <h3 className="flex items-center gap-2 text-lg font-black text-slate-900">
              <CheckCircle2 size={19} className="text-emerald-700" /> Réservations Reserve
            </h3>
            <div className="mt-4 space-y-3">
              {snapshot?.bookings.map((booking) => (
                <article key={booking.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="font-black text-slate-900">{catalogLabels.get(booking.catalogItemId) || 'Élément Reserve'}</div>
                      <div className="mt-1 text-sm text-slate-600">{formatDateTime(booking.startsAt)} – {formatDateTime(booking.endsAt)}</div>
                      <div className="mt-1 flex items-center gap-2 text-xs font-bold text-slate-700">
                        <Users size={14} /> {booking.partySize} · {BOOKING_LABELS[booking.state]}
                      </div>
                    </div>
                    {booking.state === 'confirmed' && (
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => void runCommand({
                            kind: 'mark_no_show',
                            businessId,
                            bookingId: booking.id,
                            expectedRevision: booking.revision,
                          }, 'La réservation est maintenant marquée absence.')}
                          className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-black text-amber-800 disabled:opacity-50"
                        >
                          <XCircle size={14} /> Absence
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => void runCommand({
                            kind: 'cancel_booking',
                            businessId,
                            bookingId: booking.id,
                            expectedRevision: booking.revision,
                          }, 'La réservation a été annulée et la capacité libérée.')}
                          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-black text-red-700 disabled:opacity-50"
                        >
                          Annuler
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              ))}
              {!snapshot?.bookings.length && (
                <p className="rounded-xl bg-slate-50 p-4 text-sm font-semibold text-slate-700">Aucune réservation Reserve dans les 90 prochains jours.</p>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
          <strong>{snapshot?.waitlist.length || 0}</strong> demande(s) en liste d’attente. Le paiement et l’acompte seront reliés uniquement à Giftshop dans le Lot 4R-E.
        </div>
      </div>
    </section>
  );
};

export default ReserveBookingEngine;
